import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Facebook, Download, Video, Play, AlertCircle, ExternalLink } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { facebookService, API_LIMITATIONS } from '../services/socialMediaApi';

interface FacebookVideoData {
  videoId: string;
  title: string;
  description: string;
  duration: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  videoUrl: string;
  thumbnailUrl: string;
  author: string;
  uploadDate: string;
  quality: 'HD' | 'SD';
}

const FacebookVideoDownloader: React.FC = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [videoData, setVideoData] = useState<FacebookVideoData | null>(null);
  const [downloadQuality, setDownloadQuality] = useState<'hd' | 'sd'>('hd');

  useSEO({
    title: 'Facebook Video Downloader - Download FB Videos in HD',
    description: 'Download Facebook videos in high quality for free. Save Facebook videos to your device with our easy-to-use video downloader tool.',
    keywords: 'facebook video downloader, fb video download, download facebook videos, facebook video saver',
    canonical: 'https://snap-tools.online/social/facebook-video-downloader'
  });

  // Fetch video data
  const fetchVideoData = async () => {
    if (!videoUrl.trim()) {
      toast.error('Please enter a Facebook video URL');
      return;
    }

    const videoInfo = facebookService.extractVideoInfo(videoUrl.trim());
    if (!videoInfo) {
      toast.error('Invalid Facebook video URL. Please check the URL format.');
      return;
    }

    setIsLoading(true);
    try {
      // Show API limitation notice
      toast(API_LIMITATIONS.FACEBOOK, { duration: 6000 });

      // Attempt to get video data (will throw error due to API requirements)
      await facebookService.getVideoData(videoInfo.videoId);

    } catch (error) {
      console.error('Failed to fetch video data:', error);
      
      // Show demo data with API limitation notice
      const mockData: FacebookVideoData = {
        videoId: videoInfo.videoId,
        title: '[DEMO] Facebook Video - API Required for Real Data',
        description: 'This is demo data. Real Facebook video information requires authenticated API access with approved app credentials.',
        duration: '2:34',
        views: 12500,
        likes: 850,
        comments: 120,
        shares: 45,
        videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
        thumbnailUrl: 'https://picsum.photos/640/360?random=3',
        author: 'Demo User',
        uploadDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        quality: downloadQuality === 'hd' ? 'HD' : 'SD'
      };

      setVideoData(mockData);
      toast.success('Demo data loaded. Facebook API required for real video access.');
    } finally {
      setIsLoading(false);
    }
  };

  // Download video
  const downloadVideo = async () => {
    if (!videoData) return;

    try {
      // Show download info
      toast(`Preparing ${downloadQuality.toUpperCase()} video download...`);
      
      // Try direct download first
      try {
        await fetch(videoData.videoUrl, {
          mode: 'no-cors',
          headers: {
            'Accept': 'video/*'
          }
        });
        
        const link = document.createElement('a');
        link.href = videoData.videoUrl;
        link.download = `facebook_video_${videoData.videoId}_${downloadQuality}.mp4`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`${downloadQuality.toUpperCase()} video download started! File will be saved as: facebook_video_${videoData.videoId}_${downloadQuality}.mp4`);
      } catch (fetchError) {
        // Fallback: Open in new tab
        window.open(videoData.videoUrl, '_blank', 'noopener,noreferrer');
        toast.success(`${downloadQuality.toUpperCase()} video opened in new tab. Right-click and select "Save video as..." to download.`);
      }
      
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download video. Please try again or check the URL.');
    }
  };

  // Format number
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Load example
  const loadExample = () => {
    setVideoUrl('https://www.facebook.com/watch/?v=1234567890123456');
    toast.success('Example URL loaded');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <Facebook className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Facebook Video Downloader</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Download Facebook videos in high quality for free. Save your favorite Facebook videos to watch offline.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Input Form */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facebook Video URL
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.facebook.com/watch/?v=1234567890123456"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && fetchVideoData()}
                  />
                  <button
                    onClick={loadExample}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Example
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Download Quality
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setDownloadQuality('hd')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      downloadQuality === 'hd'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <Video className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">HD Quality</div>
                    <div className="text-xs text-gray-500">1280x720</div>
                  </button>
                  <button
                    onClick={() => setDownloadQuality('sd')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      downloadQuality === 'sd'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <Video className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">SD Quality</div>
                    <div className="text-xs text-gray-500">854x480</div>
                  </button>
                </div>
              </div>

              <button
                onClick={fetchVideoData}
                disabled={isLoading || !videoUrl.trim()}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Video className="w-4 h-4" />
                    Get Video
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Video Data */}
          {videoData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-gray-200 rounded-lg p-6 mb-8"
            >
              <div className="flex flex-col md:flex-row gap-6">
                {/* Video Preview */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img
                      src={videoData.thumbnailUrl}
                      alt="Video thumbnail"
                      className="w-64 h-36 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                      <Play className="w-12 h-12 text-white" />
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                      {videoData.duration}
                    </div>
                    <div className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                      {videoData.quality}
                    </div>
                  </div>
                </div>

                {/* Video Info */}
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{videoData.title}</h3>
                  <p className="text-gray-600 mb-3">{videoData.description}</p>
                  
                  <div className="text-sm text-gray-500 mb-4">
                    <p>Author: {videoData.author}</p>
                    <p>Posted: {formatDate(videoData.uploadDate)}</p>
                    <p>Video ID: {videoData.videoId}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">{formatNumber(videoData.views)}</div>
                      <div className="text-sm text-gray-600">Views</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-red-600">{formatNumber(videoData.likes)}</div>
                      <div className="text-sm text-gray-600">Likes</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{formatNumber(videoData.comments)}</div>
                      <div className="text-sm text-gray-600">Comments</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-600">{formatNumber(videoData.shares)}</div>
                      <div className="text-sm text-gray-600">Shares</div>
                    </div>
                  </div>

                  {/* Download Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={downloadVideo}
                      className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download Video ({downloadQuality.toUpperCase()})
                    </button>
                    <button
                      onClick={() => window.open(videoUrl, '_blank')}
                      className="flex items-center justify-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View on Facebook
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 mb-1">Important Notice</h4>
                <p className="text-sm text-yellow-800">
                  This tool is for personal use only. Please respect content creators' rights and Facebook's terms of service. 
                  Only download videos you have permission to download or that are your own content.
                </p>
              </div>
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">How to Use</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Copy the Facebook video URL from your browser</li>
                <li>• Paste it into the input field above</li>
                <li>• Choose your preferred download quality</li>
                <li>• Click "Get Video" to load video information</li>
                <li>• Click "Download Video" to save to your device</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Supported URLs</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Facebook Watch videos (facebook.com/watch)</li>
                <li>• Page videos (facebook.com/page/videos/)</li>
                <li>• Profile videos (facebook.com/username/videos/)</li>
                <li>• Short URLs (fb.watch/)</li>
                <li>• Post videos (facebook.com/posts/)</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Quality Options</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• <strong>HD Quality:</strong> 1280x720 resolution</li>
                <li>• <strong>SD Quality:</strong> 854x480 resolution</li>
                <li>• Original video format (MP4)</li>
                <li>• Fast download speeds</li>
                <li>• No quality loss during processing</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-lg p-6">
              <h4 className="font-semibold text-red-900 mb-3">Legal Guidelines</h4>
              <ul className="text-sm text-red-800 space-y-2">
                <li>• Only download videos you have rights to</li>
                <li>• Respect copyright and intellectual property</li>
                <li>• Use downloads for personal purposes only</li>
                <li>• Follow Facebook's terms of service</li>
                <li>• Don't redistribute copyrighted content</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FacebookVideoDownloader;
